import { useState } from 'react';
import axios from 'axios';
import Viewer3D from './Viewer3D';
import ControlPanel from './ControlPanel';
import QuoteForm from './QuoteForm';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Configurator = () => {
  const [configuration, setConfiguration] = useState({
    profile: 'W20',
    width: 1000,
    height: 2100,
    color: 'Black',
    hardware: 'Lever Handle (Standard)',
    glass_type: 'Clear',
    door_type: 'Double',
    panel_style: 'Horizontal Lites (3 Equal)',
    lites_count: 3,
    transom: 'No',
    transomHeight: 400,
    threshold: 'Standard Threshold',
    sidelite_left: 'No',
    sidelite_right: 'No',
    arched_in_square: 'No',
    true_arch_frame: 'No'
  });

  const [isQuoteDialogOpen, setIsQuoteDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDoorOpen, setIsDoorOpen] = useState(false);
  const [isAutoRotate, setIsAutoRotate] = useState(false);

  const updateConfiguration = (key, value) => {
    setConfiguration(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleQuoteSubmit = async (customerData) => {
    setIsLoading(true);
    try {
      await axios.post(`${API}/submit-quote`, {
        configuration,
        ...customerData
      });
      toast.success('Thank you for submitting your request! Eurosteel will contact you soon.');
      setIsQuoteDialogOpen(false);
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Failed to submit quote request');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Toaster position="top-center" />
      
      {/* Mobile Layout */}
      <div className="md:hidden">
        {/* 3D Viewer - Fixed at top on mobile */}
        <div className="fixed top-0 left-0 right-0 z-20 w-full h-[40vh] bg-[#F5F5F5]">
          <div className="relative w-full h-full">
            <Viewer3D configuration={configuration} isOpen={isDoorOpen} autoRotate={isAutoRotate} />
            
            {/* Logo */}
            <div className="absolute top-2 right-2 z-10">
              <img 
                src="https://customer-assets.emergentagent.com/job_door3d-config-2/artifacts/qli81nma_White%20Minimalist%20Square%20D%20Logo%20%284000%20x%204000%20px%29%20%284000%20x%202000%20px%29%20%28500%20x%20500%20px%29.png" 
                alt="Euro Steel Logo" 
                className="h-12 w-auto drop-shadow-lg"
              />
            </div>
            
            {/* Control buttons */}
            <div className="absolute top-2 left-2 z-10 flex gap-1">
              <button
                data-testid="door-state-toggle-mobile"
                onClick={() => setIsDoorOpen(!isDoorOpen)}
                className="px-2 py-1 bg-white border border-zinc-300 rounded-none shadow-sm hover:bg-zinc-50 transition-colors font-medium text-xs"
              >
                {isDoorOpen ? 'Close' : 'Open'}
              </button>
              <button
                data-testid="auto-rotate-toggle-mobile"
                onClick={() => setIsAutoRotate(!isAutoRotate)}
                className={`px-2 py-1 border rounded-none shadow-sm transition-colors font-medium text-xs ${
                  isAutoRotate 
                    ? 'bg-primary text-primary-foreground border-primary' 
                    : 'bg-white border-zinc-300 hover:bg-zinc-50'
                }`}
              >
                {isAutoRotate ? 'Stop' : 'Rotate'}
              </button>
            </div>
          </div>
        </div>

        {/* Spacer for fixed header */}
        <div className="h-[40vh]" />

        {/* Control Panel - Scrollable on mobile */}
        <div className="bg-white pb-20">
          <ControlPanel 
            configuration={configuration}
            updateConfiguration={updateConfiguration}
          />
        </div>
        
        {/* Request Quote Button - Fixed at bottom on mobile */}
        <div className="fixed bottom-0 left-0 right-0 p-4 border-t border-zinc-200 bg-white z-30">
          <Button 
            data-testid="request-quote-btn-mobile"
            onClick={() => setIsQuoteDialogOpen(true)}
            className="w-full h-12 rounded-none text-base font-semibold"
          >
            Request Quote
          </Button>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:flex h-screen w-full flex-row overflow-hidden bg-white">
        {/* 3D Viewer - Left Side */}
        <div className="w-[65%] h-full relative bg-[#F5F5F5]">
          <Viewer3D configuration={configuration} isOpen={isDoorOpen} autoRotate={isAutoRotate} />
          
          {/* Logo */}
          <div className="absolute top-4 right-4 z-10">
            <img 
              src="https://customer-assets.emergentagent.com/job_door3d-config-2/artifacts/qli81nma_White%20Minimalist%20Square%20D%20Logo%20%284000%20x%204000%20px%29%20%284000%20x%202000%20px%29%20%28500%20x%20500%20px%29.png" 
              alt="Euro Steel Logo" 
              className="h-24 w-auto drop-shadow-lg"
            />
          </div>
          
          {/* Control buttons */}
          <div className="absolute top-4 left-4 z-10 flex gap-2">
            <button
              data-testid="door-state-toggle"
              onClick={() => setIsDoorOpen(!isDoorOpen)}
              className="px-4 py-2 bg-white border border-zinc-300 rounded-none shadow-sm hover:bg-zinc-50 transition-colors font-medium text-sm"
            >
              {isDoorOpen ? 'Close Door' : 'Open Door'}
            </button>
            <button
              data-testid="auto-rotate-toggle"
              onClick={() => setIsAutoRotate(!isAutoRotate)}
              className={`px-4 py-2 border rounded-none shadow-sm transition-colors font-medium text-sm ${
                isAutoRotate 
                  ? 'bg-primary text-primary-foreground border-primary' 
                  : 'bg-white border-zinc-300 hover:bg-zinc-50'
              }`}
            >
              {isAutoRotate ? 'Stop Rotation' : 'Auto Rotate'}
            </button>
          </div>
        </div>

        {/* Control Panel - Right Side */}
        <div className="w-[35%] h-full flex flex-col bg-white border-l border-zinc-200">
          <div className="flex-1 overflow-y-auto">
            <ControlPanel 
              configuration={configuration}
              updateConfiguration={updateConfiguration}
            />
          </div>
          
          {/* Request Quote Button */}
          <div className="p-6 border-t border-zinc-200">
            <Button 
              data-testid="request-quote-btn"
              onClick={() => setIsQuoteDialogOpen(true)}
              className="w-full h-12 rounded-none text-base font-semibold"
            >
              Request Quote
            </Button>
          </div>
        </div>
      </div>

      <QuoteForm
        isOpen={isQuoteDialogOpen}
        onClose={() => setIsQuoteDialogOpen(false)}
        onSubmit={handleQuoteSubmit}
        configuration={configuration}
        isLoading={isLoading}
      />
    </>
  );
};

export default Configurator;
